import React, { useState, useEffect, useCallback } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { FiRefreshCw, FiAlertTriangle, FiDroplet, FiActivity, FiMapPin, FiTrendingUp, FiTrendingDown, FiMinus, FiGlobe } from 'react-icons/fi';
import { toast } from 'react-toastify';
import floodApi from '../services/floodApi';
import { useAuth } from '../context/AuthContext';
import './FloodDashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Common country/region codes
const REGION_OPTIONS = [
  { code: 'ZA', name: 'South Africa' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'IN', name: 'India' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BR', name: 'Brazil' },
  { code: 'US', name: 'United States' },
  { code: 'AU', name: 'Australia' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'KE', name: 'Kenya' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Peru' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'TH', name: 'Thailand' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'MX', name: 'Mexico' },
  { code: 'AR', name: 'Argentina' },
];

const SEVERITY_COLORS = {
  EXTREME: '#9b59b6',
  SEVERE: '#e74c3c',
  ABOVE_NORMAL: '#f39c12',
  NO_FLOODING: '#27ae60',
  UNKNOWN: '#95a5a6',
};

const SEVERITY_LABELS = {
  EXTREME: 'Extreme',
  SEVERE: 'Severe',
  ABOVE_NORMAL: 'Above Normal',
  NO_FLOODING: 'No Flooding',
  UNKNOWN: 'Unknown',
};

const TREND_ICONS = {
  RISE: <FiTrendingUp />,
  FALL: <FiTrendingDown />,
  NO_CHANGE: <FiMinus />,
};

const FloodDashboard = () => {
  const [regionCode, setRegionCode] = useState('ZA');
  const [loading, setLoading] = useState(false);
  const [overviewData, setOverviewData] = useState(null);
  const [significantEvents, setSignificantEvents] = useState([]);
  const [selectedGauge, setSelectedGauge] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const { user } = useAuth();
  const lowDataMode = user?.preferences?.lowDataMode || false;

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const data = await floodApi.getFloodOverview(regionCode);
      if (data.success) {
        setOverviewData(data);
        toast.success(`Loaded flood data for ${regionCode}`);
      } else {
        toast.error(data.error || 'Failed to load flood data');
      }
    } catch (err) {
      toast.error('Error fetching flood overview');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [regionCode]);

  const fetchSignificantEvents = useCallback(async () => {
    try {
      const data = await floodApi.getSignificantEvents();
      if (data.success) {
        setSignificantEvents(data.significantEvents || []);
      }
    } catch (err) {
      console.error('Error fetching significant events:', err);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
    if (!lowDataMode) {
      fetchSignificantEvents();
    }
  }, [fetchOverview, fetchSignificantEvents, lowDataMode]);

  const handleGaugeClick = async (gauge) => {
    setSelectedGauge(gauge);
    setForecastLoading(true);
    try {
      const gaugeId = gauge.gaugeId;
      const data = await floodApi.getGaugeForecasts([gaugeId]);
      if (data.success) {
        setForecastData(data.forecasts[gaugeId] || null);
      }
    } catch (err) {
      console.error('Error fetching forecast:', err);
    } finally {
      setForecastLoading(false);
    }
  };

  // ── Chart Data ──

  const severityChartData = overviewData?.summary ? {
    labels: Object.keys(overviewData.summary.severityCounts).map(k => SEVERITY_LABELS[k] || k),
    datasets: [{
      data: Object.values(overviewData.summary.severityCounts),
      backgroundColor: Object.keys(overviewData.summary.severityCounts).map(k => SEVERITY_COLORS[k] || '#999'),
      borderWidth: 0,
      hoverOffset: 8,
    }],
  } : null;

  const trendChartData = overviewData?.summary ? {
    labels: ['Rising', 'Falling', 'No Change'],
    datasets: [{
      label: 'Gauge Trends',
      data: [
        overviewData.summary.trendCounts.RISE || 0,
        overviewData.summary.trendCounts.FALL || 0,
        overviewData.summary.trendCounts.NO_CHANGE || 0,
      ],
      backgroundColor: ['#e74c3c', '#27ae60', '#3498db'],
      borderRadius: 6,
      borderWidth: 0,
    }],
  } : null;

  // Build a timeline chart from flood statuses by issued date
  const timelineChartData = (() => {
    if (!overviewData?.floodStatuses?.length) return null;

    const dateMap = {};
    overviewData.floodStatuses.forEach(status => {
      if (!status.issuedTime) return;
      const date = status.issuedTime.split('T')[0];
      if (!dateMap[date]) dateMap[date] = { EXTREME: 0, SEVERE: 0, ABOVE_NORMAL: 0, NO_FLOODING: 0, UNKNOWN: 0 };
      if (dateMap[date][status.severity] !== undefined) {
        dateMap[date][status.severity]++;
      }
    });

    const sortedDates = Object.keys(dateMap).sort();
    if (sortedDates.length === 0) return null;

    return {
      labels: sortedDates,
      datasets: [
        {
          label: 'Extreme',
          data: sortedDates.map(d => dateMap[d].EXTREME),
          borderColor: SEVERITY_COLORS.EXTREME,
          backgroundColor: SEVERITY_COLORS.EXTREME + '33',
          fill: true,
          tension: 0.3,
        },
        {
          label: 'Severe',
          data: sortedDates.map(d => dateMap[d].SEVERE),
          borderColor: SEVERITY_COLORS.SEVERE,
          backgroundColor: SEVERITY_COLORS.SEVERE + '33',
          fill: true,
          tension: 0.3,
        },
        {
          label: 'Above Normal',
          data: sortedDates.map(d => dateMap[d].ABOVE_NORMAL),
          borderColor: SEVERITY_COLORS.ABOVE_NORMAL,
          backgroundColor: SEVERITY_COLORS.ABOVE_NORMAL + '33',
          fill: true,
          tension: 0.3,
        },
      ],
    };
  })();

  // Forecast chart from selected gauge
  const forecastChartData = (() => {
    if (!forecastData?.forecasts?.length) return null;

    const forecasts = forecastData.forecasts.slice(0, 20); // limit for readability
    const labels = forecasts.map((f, i) => {
      const time = f.issuedTime ? new Date(f.issuedTime).toLocaleDateString() : `#${i + 1}`;
      return time;
    });

    // Extract forecast ranges if available
    const hasRanges = forecasts.some(f => f.forecastRanges?.length > 0);

    if (hasRanges) {
      // Use first forecast range from each
      const values = forecasts.map(f => {
        const range = f.forecastRanges?.[0];
        return range?.value ?? 0;
      });

      return {
        labels,
        datasets: [{
          label: `Forecast - ${selectedGauge?.gaugeId || 'Gauge'}`,
          data: values,
          borderColor: '#3498db',
          backgroundColor: '#3498db33',
          fill: true,
          tension: 0.4,
          pointRadius: 3,
        }],
      };
    }

    return null;
  })();

  // ── Helpers ──
  const getSeverityBadge = (severity) => {
    const color = SEVERITY_COLORS[severity] || '#999';
    const label = SEVERITY_LABELS[severity] || severity;
    return (
      <span className="severity-badge" style={{ backgroundColor: color }}>
        {label}
      </span>
    );
  };

  const getTrendIcon = (trend) => {
    const icon = TREND_ICONS[trend];
    return icon || <FiMinus />;
  };

  // Filter active flooding statuses
  const activeFloodStatuses = (overviewData?.floodStatuses || []).filter(
    s => s.severity === 'EXTREME' || s.severity === 'SEVERE' || s.severity === 'ABOVE_NORMAL'
  );

  return (
    <div className="flood-dashboard">
      {/* Controls Bar */}
      <div className="flood-controls">
        <div className="flood-controls-left">
          <h2><FiDroplet /> Flood Forecasting</h2>
          <span className="flood-source">Google Flood Forecasting API</span>
        </div>
        <div className="flood-controls-right">
          <select
            value={regionCode}
            onChange={(e) => setRegionCode(e.target.value)}
            className="region-select"
          >
            {REGION_OPTIONS.map(r => (
              <option key={r.code} value={r.code}>{r.name} ({r.code})</option>
            ))}
          </select>
          <button className="btn-refresh" onClick={fetchOverview} disabled={loading}>
            <FiRefreshCw className={loading ? 'spinning' : ''} />
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flood-tabs">
        <button className={`flood-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
          <FiActivity /> Overview
        </button>
        <button className={`flood-tab ${activeTab === 'gauges' ? 'active' : ''}`} onClick={() => setActiveTab('gauges')}>
          <FiMapPin /> Gauges & Forecasts
        </button>
        <button className={`flood-tab ${activeTab === 'events' ? 'active' : ''}`} onClick={() => setActiveTab('events')}>
          <FiAlertTriangle /> Significant Events
        </button>
      </div>

      {loading && (
        <div className="flood-loading">
          <div className="flood-spinner"></div>
          <p>Fetching flood data for {regionCode}...</p>
        </div>
      )}

      {!loading && activeTab === 'overview' && overviewData && (
        <div className="flood-overview">
          {/* Summary Cards */}
          <div className="flood-summary-cards">
            <div className="flood-card summary-card">
              <div className="card-icon gauges-icon"><FiMapPin /></div>
              <div className="card-info">
                <span className="card-value">{overviewData.summary.totalGauges}</span>
                <span className="card-label">Total Gauges</span>
              </div>
            </div>
            <div className="flood-card summary-card">
              <div className="card-icon statuses-icon"><FiDroplet /></div>
              <div className="card-info">
                <span className="card-value">{overviewData.summary.totalFloodStatuses}</span>
                <span className="card-label">Flood Statuses</span>
              </div>
            </div>
            <div className={`flood-card summary-card ${overviewData.summary.hasActiveFlooding ? 'alert-card' : ''}`}>
              <div className="card-icon alert-icon"><FiAlertTriangle /></div>
              <div className="card-info">
                <span className="card-value">
                  {overviewData.summary.severityCounts.EXTREME + overviewData.summary.severityCounts.SEVERE + overviewData.summary.severityCounts.ABOVE_NORMAL}
                </span>
                <span className="card-label">Active Alerts</span>
              </div>
            </div>
            <div className="flood-card summary-card">
              <div className="card-icon events-icon"><FiGlobe /></div>
              <div className="card-info">
                <span className="card-value">{overviewData.summary.totalSignificantEvents}</span>
                <span className="card-label">Significant Events (Global)</span>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="flood-charts-row">
            {/* Severity Doughnut */}
            <div className="flood-card chart-card">
              <h3>Severity Distribution</h3>
              {severityChartData && (
                <div className="chart-container doughnut-container">
                  <Doughnut
                    data={severityChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: { color: '#ccc', padding: 12, usePointStyle: true },
                        },
                      },
                    }}
                  />
                </div>
              )}
            </div>

            {/* Trend Bar Chart */}
            <div className="flood-card chart-card">
              <h3>Forecast Trends</h3>
              {trendChartData && (
                <div className="chart-container">
                  <Bar
                    data={trendChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: { color: '#aaa' },
                          grid: { color: 'rgba(255,255,255,0.05)' },
                        },
                        x: {
                          ticks: { color: '#aaa' },
                          grid: { display: false },
                        },
                      },
                    }}
                  />
                </div>
              )}
            </div>

            {/* Timeline Line Chart */}
            <div className="flood-card chart-card wide-chart">
              <h3>Flood Status Timeline</h3>
              {timelineChartData ? (
                <div className="chart-container">
                  <Line
                    data={timelineChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'top',
                          labels: { color: '#ccc', usePointStyle: true },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: { color: '#aaa' },
                          grid: { color: 'rgba(255,255,255,0.05)' },
                        },
                        x: {
                          ticks: { color: '#aaa', maxRotation: 45 },
                          grid: { display: false },
                        },
                      },
                    }}
                  />
                </div>
              ) : (
                <p className="no-data-text">No timeline data available</p>
              )}
            </div>
          </div>

          {/* Active Flooding Table */}
          <div className="flood-card table-card">
            <h3><FiAlertTriangle /> Active Flood Alerts ({activeFloodStatuses.length})</h3>
            {activeFloodStatuses.length > 0 ? (
              <div className="flood-table-wrapper">
                <table className="flood-table">
                  <thead>
                    <tr>
                      <th>Gauge ID</th>
                      <th>Severity</th>
                      <th>Trend</th>
                      <th>Location</th>
                      <th>Issued</th>
                      <th>Forecast Period</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeFloodStatuses.slice(0, 50).map((status, idx) => (
                      <tr key={idx} className={`severity-row severity-${status.severity?.toLowerCase()}`}>
                        <td className="gauge-id">{status.gaugeId}</td>
                        <td>{getSeverityBadge(status.severity)}</td>
                        <td className="trend-cell">
                          {getTrendIcon(status.forecastTrend)}
                          <span>{status.forecastTrend || 'N/A'}</span>
                        </td>
                        <td>
                          {status.gaugeLocation
                            ? `${status.gaugeLocation.latitude?.toFixed(3)}, ${status.gaugeLocation.longitude?.toFixed(3)}`
                            : 'N/A'
                          }
                        </td>
                        <td>{status.issuedTime ? new Date(status.issuedTime).toLocaleDateString() : 'N/A'}</td>
                        <td>
                          {status.forecastTimeRange
                            ? `${new Date(status.forecastTimeRange.start).toLocaleDateString()} – ${new Date(status.forecastTimeRange.end).toLocaleDateString()}`
                            : 'N/A'
                          }
                        </td>
                        <td>{status.source || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data-text">No active flood alerts in this region 🎉</p>
            )}
          </div>
        </div>
      )}

      {!loading && activeTab === 'gauges' && overviewData && (
        <div className="flood-gauges-section">
          <div className="flood-gauges-layout">
            {/* Gauges List */}
            <div className="flood-card gauges-list-card">
              <h3><FiMapPin /> Gauges in {regionCode} ({overviewData.gauges?.length || 0})</h3>
              <div className="gauges-list">
                {(overviewData.floodStatuses || []).slice(0, 100).map((status, idx) => (
                  <div
                    key={idx}
                    className={`gauge-item ${selectedGauge?.gaugeId === status.gaugeId ? 'selected' : ''}`}
                    onClick={() => handleGaugeClick(status)}
                  >
                    <div className="gauge-item-header">
                      <span className="gauge-item-id">{status.gaugeId}</span>
                      {getSeverityBadge(status.severity)}
                    </div>
                    <div className="gauge-item-details">
                      <span className="gauge-trend">
                        {getTrendIcon(status.forecastTrend)} {status.forecastTrend || 'N/A'}
                      </span>
                      {status.gaugeLocation && (
                        <span className="gauge-location">
                          📍 {status.gaugeLocation.latitude?.toFixed(2)}, {status.gaugeLocation.longitude?.toFixed(2)}
                        </span>
                      )}
                    </div>
                    {status.forecastChange?.valueChange && (
                      <div className="gauge-value-change">
                        Change: {status.forecastChange.valueChange.lowerBound?.toFixed(2)}m – {status.forecastChange.valueChange.upperBound?.toFixed(2)}m
                      </div>
                    )}
                    {status.source && <span className="gauge-source">Source: {status.source}</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Forecast Detail Panel */}
            <div className="flood-card forecast-detail-card">
              <h3><FiActivity /> Gauge Forecast</h3>
              {!selectedGauge && (
                <div className="no-selection">
                  <FiMapPin size={48} />
                  <p>Select a gauge from the list to view its forecast</p>
                </div>
              )}
              {selectedGauge && (
                <div className="gauge-detail">
                  <div className="gauge-detail-header">
                    <h4>{selectedGauge.gaugeId}</h4>
                    {getSeverityBadge(selectedGauge.severity)}
                  </div>

                  <div className="gauge-detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Trend</span>
                      <span className="detail-value">
                        {getTrendIcon(selectedGauge.forecastTrend)} {selectedGauge.forecastTrend || 'N/A'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Quality Verified</span>
                      <span className="detail-value">{selectedGauge.qualityVerified ? '✅ Yes' : '⚠️ No'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Inference Type</span>
                      <span className="detail-value">{selectedGauge.mapInferenceType || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Issued</span>
                      <span className="detail-value">
                        {selectedGauge.issuedTime ? new Date(selectedGauge.issuedTime).toLocaleString() : 'N/A'}
                      </span>
                    </div>
                    {selectedGauge.forecastTimeRange && (
                      <div className="detail-item full-width">
                        <span className="detail-label">Forecast Period</span>
                        <span className="detail-value">
                          {new Date(selectedGauge.forecastTimeRange.start).toLocaleDateString()} – {new Date(selectedGauge.forecastTimeRange.end).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {selectedGauge.forecastChange?.valueChange && (
                      <div className="detail-item full-width">
                        <span className="detail-label">Water Level Change</span>
                        <span className="detail-value change-value">
                          {selectedGauge.forecastChange.valueChange.lowerBound?.toFixed(3)}m – {selectedGauge.forecastChange.valueChange.upperBound?.toFixed(3)}m
                        </span>
                      </div>
                    )}
                    {selectedGauge.inundationMapSet && (
                      <div className="detail-item full-width">
                        <span className="detail-label">Inundation Maps</span>
                        <div className="inundation-levels">
                          {selectedGauge.inundationMapSet.inundationMaps?.map((m, i) => (
                            <span key={i} className={`inundation-badge level-${m.level?.toLowerCase()}`}>
                              {m.level}
                            </span>
                          ))}
                          <span className="inundation-type">
                            Type: {selectedGauge.inundationMapSet.inundationMapType || 'N/A'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Forecast Chart */}
                  {forecastLoading && <div className="forecast-loading">Loading forecast data...</div>}
                  {!forecastLoading && forecastChartData && (
                    <div className="chart-container forecast-chart">
                      <Line
                        data={forecastChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              labels: { color: '#ccc' },
                            },
                          },
                          scales: {
                            y: {
                              ticks: { color: '#aaa' },
                              grid: { color: 'rgba(255,255,255,0.05)' },
                            },
                            x: {
                              ticks: { color: '#aaa', maxRotation: 45 },
                              grid: { display: false },
                            },
                          },
                        }}
                      />
                    </div>
                  )}
                  {!forecastLoading && !forecastChartData && forecastData && (
                    <div className="forecast-raw">
                      <h5>Raw Forecast Data</h5>
                      <pre>{JSON.stringify(forecastData, null, 2)}</pre>
                    </div>
                  )}
                  {!forecastLoading && !forecastData && (
                    <p className="no-data-text">No forecast data available for this gauge</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && activeTab === 'events' && (
        <div className="flood-events-section">
          <div className="flood-card table-card">
            <h3><FiGlobe /> Significant Flood Events ({significantEvents.length})</h3>
            {significantEvents.length > 0 ? (
              <div className="significant-events-list">
                {significantEvents.map((event, idx) => {
                  // Parse event fields from Google Flood API structure
                  const startTime = event.eventInterval?.startTime || event.startTime;
                  const endTime = event.eventInterval?.minimumEndTime || event.eventInterval?.endTime || event.endTime;
                  const countries = event.affectedCountryCodes || event.countries || [];
                  const area = event.areaKm2 || event.area;
                  const population = event.populationCount || event.population;
                  const eventId = event.eventId || event.id || `Event ${idx + 1}`;
                  const severity = event.severity || event.overallSeverity;

                  // Build country display
                  const countryDisplay = Array.isArray(countries) && countries.length > 0
                    ? countries.join(', ')
                    : typeof countries === 'string' ? countries : 'Global';

                  // Format dates
                  const formatDate = (dateStr) => {
                    if (!dateStr) return 'N/A';
                    try { return new Date(dateStr).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }); }
                    catch { return dateStr; }
                  };

                  // Calculate duration
                  let durationText = '';
                  if (startTime && endTime) {
                    try {
                      const diffMs = new Date(endTime) - new Date(startTime);
                      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
                      durationText = diffDays > 0 ? `${diffDays} days` : '< 1 day';
                    } catch { durationText = ''; }
                  }

                  return (
                    <div key={idx} className="significant-event-card">
                      <div className="event-card-header">
                        <div className="event-id-badge">
                          <FiAlertTriangle />
                          <span>{eventId}</span>
                        </div>
                        {severity && (
                          <span className={`severity-badge severity-${(severity || '').toLowerCase()}`} style={{ backgroundColor: severity === 'EXTREME' ? '#9b59b6' : severity === 'SEVERE' ? '#e74c3c' : '#f39c12' }}>
                            {severity}
                          </span>
                        )}
                      </div>

                      <div className="event-card-body">
                        <div className="event-detail-row">
                          <span className="event-detail-icon">🌍</span>
                          <div className="event-detail-content">
                            <span className="event-detail-label">Affected Countries</span>
                            <span className="event-detail-value">{countryDisplay}</span>
                          </div>
                        </div>

                        <div className="event-detail-row">
                          <span className="event-detail-icon">📅</span>
                          <div className="event-detail-content">
                            <span className="event-detail-label">Event Period</span>
                            <span className="event-detail-value">
                              {formatDate(startTime)} — {formatDate(endTime)}
                              {durationText && <span className="event-duration">({durationText})</span>}
                            </span>
                          </div>
                        </div>

                        <div className="event-stats-row">
                          <div className="event-stat">
                            <span className="event-stat-icon">📐</span>
                            <span className="event-stat-value">{area ? `${Number(area).toLocaleString()} km²` : 'N/A'}</span>
                            <span className="event-stat-label">Area Affected</span>
                          </div>
                          <div className="event-stat">
                            <span className="event-stat-icon">👥</span>
                            <span className="event-stat-value">{population ? Number(population).toLocaleString() : 'N/A'}</span>
                            <span className="event-stat-label">Population Affected</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-events">
                <FiGlobe size={48} />
                <p>No significant flood events reported currently</p>
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && !overviewData && (
        <div className="flood-empty">
          <FiDroplet size={64} />
          <h3>No flood data loaded</h3>
          <p>Select a region and click Refresh to load flood forecasting data.</p>
        </div>
      )}
    </div>
  );
};

export default FloodDashboard;
